
import { useState, useEffect } from 'react'
import {
  Box,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  IconButton,
} from '@chakra-ui/react'
import { X, Blocks, Settings } from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { BlockLibrary } from './BlockLibrary'
import { BlockSettings } from './BlockSettings'
import type { PageBlock, BlockType } from '@/types/pageBuilder'

interface RightSidebarProps {
  isOpen: boolean
  onClose: () => void
  onSelectBlock: (type: BlockType) => void
  selectedBlock: PageBlock | null
  onUpdateBlock: (content: Record<string, any>) => void
  onCloseSettings: () => void
  searchQuery: string
  onSearchChange: (query: string) => void
}

export function RightSidebar({
  isOpen,
  onClose,
  onSelectBlock,
  selectedBlock,
  onUpdateBlock,
  onCloseSettings,
  searchQuery,
  onSearchChange,
}: RightSidebarProps) {
  const colors = useThemeColors()
  const [activeTab, setActiveTab] = useState(0)

  // Auto-switch to Settings tab when a block is selected
  useEffect(() => {
    if (selectedBlock) {
      setActiveTab(1) // Switch to Settings tab
    }
  }, [selectedBlock])

  return (
    <Box
      w="400px"
      h="100%"
      borderLeft="1px"
      borderColor={colors.border}
      bg={colors.cardBg}
      display="flex"
      flexDirection="column"
      position="absolute"
      right={0}
      top={0}
      bottom={0}
      boxShadow="lg"
      transform={isOpen ? 'translateX(0)' : 'translateX(100%)'}
      transition="transform 0.3s ease"
      zIndex={20}
    >
      {/* Header */}
      <Box
        p={3}
        borderBottom="1px"
        borderColor={colors.border}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <Tabs index={activeTab} onChange={setActiveTab} variant="enclosed" size="sm" flex="1">
          <TabList>
            <Tab fontSize="xs" px={3}>
              <Blocks size={14} style={{ marginRight: '6px', display: 'inline' }} />
              Blocks
            </Tab>
            <Tab fontSize="xs" px={3}>
              <Settings size={14} style={{ marginRight: '6px', display: 'inline' }} />
              Settings
            </Tab>
          </TabList>
        </Tabs>
        <IconButton
          aria-label="Close sidebar"
          icon={<X size={16} />}
          size="xs"
          variant="ghost"
          onClick={onClose}
          ml={2}
        />
      </Box>

      {/* Content */}
      <Box flex="1" overflow="hidden" display="flex" flexDirection="column">
        <Tabs index={activeTab} onChange={setActiveTab} variant="unstyled" display="flex" flexDirection="column" h="100%">
          <TabPanels flex="1" overflow="hidden" h="100%">
            {/* Blocks Tab */}
            <TabPanel p={0} h="100%" overflow="hidden">
              <BlockLibrary
                onSelectBlock={onSelectBlock}
                searchQuery={searchQuery}
                onSearchChange={onSearchChange}
              />
            </TabPanel>

            {/* Settings Tab */}
            <TabPanel p={0} h="100%" overflow="hidden">
              <BlockSettings
                block={selectedBlock}
                onUpdate={onUpdateBlock}
                onClose={onCloseSettings}
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </Box>
  )
}

